'use client'

import { storyblokEditable } from '@storyblok/js'
import cn from 'clsx'
import { useWindowSize } from 'hamo'
import { useLenis } from 'lenis/react'
import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTempus } from 'tempus/react'

type AnimateFunction = {
  animate: () => number
}

type Block = {
  _uid: string
  _editable: string
  lines: {
    line: string
    _uid: string
    _editable: string
  }[]
}

type TerminalBlockProps = {
  lines: Block[]
  ref?: React.RefObject<AnimateFunction>
  animationId: string
}

type TerminalLineProps = {
  chars: string[]
  ref?: React.RefObject<AnimateFunction>
  animationId: string
}

type TerminalBlock = {
  block: string[]
  ref?: React.RefObject<AnimateFunction>
}

type TerminalLine = {
  chars: string[]
  animationId: string
  ref?: React.RefObject<AnimateFunction>
}

function TerminalLine({ chars, animationId, ref }: TerminalLineProps) {
  const charsRef = useRef<Array<HTMLSpanElement | null>>([])
  const progressRef = useRef<number>(0)

  useImperativeHandle(
    ref,
    () => ({
      animate: () => {
        if (progressRef.current >= chars.length) return 1

        const index = progressRef.current
        charsRef.current[index]?.style?.setProperty('opacity', '1')
        progressRef.current += 1

        return 0
      },
    }),
    [chars]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (sessionStorage.getItem(animationId) === 'true') {
      for (const char of charsRef.current) {
        char?.style?.setProperty('opacity', '1')
      }
    }
  }, [animationId])

  return (
    <span className="relative flex dr-gap-0.5 flex-wrap">
      {chars.map((char, index) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          key={index}
          ref={(el) => {
            charsRef.current[index] = el
          }}
          style={{
            opacity: 0,
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  )
}

function TerminalBlock({ lines, animationId, ref }: TerminalBlockProps) {
  const lenis = useLenis()
  const { height: windowHeight } = useWindowSize()
  const blockRef = useRef<HTMLUListElement>(null)
  const currentLineRef = useRef<number>(0)
  const lineRefs = useRef<Array<AnimateFunction | null>>([])

  useImperativeHandle(
    ref,
    () => ({
      scrollTo: () => {
        if (blockRef.current) {
          lenis?.scrollTo?.(blockRef.current, {
            offset: -0.5 * (windowHeight ?? 0),
          })
        }
      },
      animate: () => {
        if (currentLineRef.current >= lines.length) return 1

        // Animate current line
        const lineCompleted =
          lineRefs.current[currentLineRef.current]?.animate() ?? 0

        // Jump to next block
        if (lineCompleted === 1) {
          currentLineRef.current += 1

          // If last line, return 1
          if (currentLineRef.current >= lines.length) return 1
        }

        return 0
      },
    }),
    [lines, lenis, windowHeight]
  )

  return (
    <ul ref={blockRef}>
      {lines.map(({ line, _uid, _editable }: Block, index: number) => (
        <li
          key={_uid}
          className="relative flex flex-col"
          {...(!!_editable && storyblokEditable({ _editable }))}
        >
          <TerminalLine
            animationId={animationId}
            ref={(el) => {
              lineRefs.current[index] = el
            }}
            chars={line.split('')}
          />
        </li>
      ))}
    </ul>
  )
}

export function TerminalAnimation({
  data,
  className,
  id,
  scrollTo = false,
}: {
  data: Block[]
  className: string
  id: string
  scrollTo?: boolean
}) {
  const currentBlockRef = useRef<number>(0)
  const blockRefs = useRef<
    Array<(AnimateFunction & { scrollTo: () => void }) | null>
  >([])
  const [animationId] = useState(`terminalAnimationPlayed-${id}`)

  useTempus(() => {
    if (currentBlockRef.current === -1) return

    if (typeof window === 'undefined') return

    if (sessionStorage.getItem(animationId) === 'true') return

    // If last block, set session storage and return
    if (currentBlockRef.current >= data.length) {
      currentBlockRef.current = -1
      sessionStorage.setItem(animationId, 'true')
      return
    }

    const index = currentBlockRef.current
    const blockCompleted = blockRefs.current?.[index]?.animate() ?? 0

    // Next block
    if (blockCompleted === 1) {
      if (scrollTo) {
        blockRefs.current?.[index]?.scrollTo()
      }
      currentBlockRef.current += 1
    }
  })

  return (
    <div className={cn(className)}>
      {data?.map((block: Block, index: number) => (
        <TerminalBlock
          index={index}
          key={block._uid}
          animationId={animationId}
          scrollTo={scrollTo}
          {...block}
          ref={(el) => {
            if (el) {
              blockRefs.current[index] = el
            }
          }}
        />
      ))}
    </div>
  )
}
